import {
  ClientAuthError,
  InteractionRequiredAuthError,
  PublicClientApplication,
} from '@azure/msal-browser'

import { AppError } from '../components/common/AppError'
import { getAcquireTokenOptions, getSignInOptions } from '../config'
import Identity from '../model/identity'
import { store } from '../redux/configureStore'
import { IRootState } from '../redux/reducers'
import InteractiveSignInRequired from '../utils'

class AuthService {
  private static instance: AuthService | undefined = undefined
  msalInstance!: PublicClientApplication
  state: IRootState = store.getState()

  private constructor() {}

  static getInstance(): AuthService {
    if (AuthService.instance === undefined) {
      AuthService.instance = new AuthService()
    }
    return AuthService.instance
  }

  setMsalInstance(msalInstance: PublicClientApplication) {
    this.msalInstance = msalInstance
  }

  private ensureMsalInstanceSet() {
    if (!this.msalInstance) {
      throw new AppError({
        name: 'MSALInstanceError',
        message:
          'MSAL instance is not initialized. Please initialize the MSAL instance before using it.',
        errorCode: 0,
        innerError: null,
      })
    }
  }

  async signIn() {
    this.ensureMsalInstanceSet()
    try {
      await this.msalInstance.loginPopup(
        getSignInOptions(this.state.configuration),
      )
    } catch (error: any) {
      if (error.errorCode == 'popup_window_error') {
        await this.msalInstance.loginRedirect(
          getSignInOptions(this.state.configuration),
        )
      } else {
        throw error
      }
    }
  }

  signOut() {
    this.ensureMsalInstanceSet()
    this.msalInstance.logout()
  }

  async getIdentity() {
    this.ensureMsalInstanceSet()
    const accounts = this.msalInstance.getAllAccounts()
    if (accounts.length > 0) {
      try {
        const response = await this.msalInstance.acquireTokenSilent({
          ...getAcquireTokenOptions(this.state.configuration),
          account: accounts[0],
        })
        return new Identity(response)
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          throw new InteractiveSignInRequired()
        }
        if (error instanceof ClientAuthError) {
          if (error.errorCode === 'block_token_requests') {
            throw new InteractiveSignInRequired()
          }
          console.warn('ClientAuthError: error code = ', error.errorCode)
        }
        throw error
      }
    }
    throw new InteractiveSignInRequired()
  }
}

const authService: AuthService = AuthService.getInstance()

export default authService
